import type { Ref } from "vue";
import type { RoutingConfig } from "./routing-fetch";

export type ServerRoutingOptions = {
  fetch?: typeof globalThis.fetch;
  pollEveryMs?: number;
  timeoutMs?: number;
  now?: () => number;
};

/**
 * Polls `/api/routing` and points every routed request at the server the
 * database names. Asks the primary first and the secondary whenever the
 * primary does not return a successful, well-formed routing answer; both read
 * the same database row. Only a successful, well-formed answer changes the
 * route; failed refreshes keep the current one.
 *
 * `shop.getUpdates` carries the same value; feeding it to `apply()` skips the
 * next scheduled poll, so the endpoint is only hit when shop polling stalls.
 */
export const createServerRoutingController = (
  config: Ref<RoutingConfig>,
  options: ServerRoutingOptions = {},
) => {
  const pollEveryMs = options.pollEveryMs ?? 60_000;
  const timeoutMs = options.timeoutMs ?? 10_000;
  const now = options.now ?? Date.now;
  let pollTimer: ReturnType<typeof setInterval> | undefined;
  let activeRequest: AbortController | undefined;
  let lastAppliedAt = 0;
  let startup: Promise<void> | undefined;

  /** Apply a value from any trusted response; ignores unknown values. */
  const apply = (server: unknown) => {
    if (server === "secondary") {
      config.value.forceServerId = "secondary";
    } else if (server === "primary") {
      config.value.forceServerId = null;
    } else {
      return;
    }
    lastAppliedAt = now();
  };

  // One server, with its own timeout. Throws on network errors, timeouts,
  // non-200 responses, malformed answers and caller aborts so the primary can
  // fail over to the secondary.
  const askServer = async (baseUrl: string, signal: AbortSignal) => {
    const attempt = new AbortController();
    const onAbort = () => attempt.abort();
    signal.addEventListener("abort", onAbort, { once: true });
    const timer = setTimeout(() => attempt.abort(), timeoutMs);
    try {
      const fetch = options.fetch ?? globalThis.fetch;
      const response = await fetch(
        new URL("/api/routing", baseUrl === "/" ? location.origin : baseUrl),
        { cache: "no-store", credentials: "include", signal: attempt.signal },
      );
      if (response.status !== 200) {
        throw new Error(`Routing server responded with ${response.status}`);
      }
      const body = (await response.json().catch(() => null)) as {
        server?: unknown;
      } | null;
      if (body?.server !== "primary" && body?.server !== "secondary") {
        throw new Error("Routing server returned an invalid response");
      }
      return body.server;
    } finally {
      clearTimeout(timer);
      signal.removeEventListener("abort", onAbort);
    }
  };

  const refresh = async () => {
    if (activeRequest) return;
    const controller = new AbortController();
    activeRequest = controller;
    const { primary, secondary } = config.value.servers;
    try {
      let server: unknown;
      try {
        server = await askServer(primary.baseUrl, controller.signal);
      } catch {
        if (controller.signal.aborted) return;
        server = await askServer(secondary.baseUrl, controller.signal);
      }
      if (!controller.signal.aborted) apply(server);
    } catch {
      // A failed refresh must never change the current route.
    } finally {
      if (activeRequest === controller) activeRequest = undefined;
    }
  };

  const start = () => {
    if (pollTimer) return;
    // The first check gates app requests (see `ready`). It is bounded by the
    // per-server timeouts and never rejects.
    startup = refresh();
    pollTimer = setInterval(() => {
      if (now() - lastAppliedAt < pollEveryMs) return;
      void refresh();
    }, pollEveryMs);
  };

  const stop = () => {
    activeRequest?.abort();
    activeRequest = undefined;
    if (pollTimer) clearInterval(pollTimer);
    pollTimer = undefined;
  };

  /**
   * Resolves once the first check after `start()` settles, so boot requests
   * already go to the right server. Resolves immediately when polling was
   * never started.
   */
  const ready = (): Promise<void> => startup ?? Promise.resolve();

  return { apply, ready, refresh, start, stop };
};
